import { useEffect, useRef, useState } from "react";
import { PiXFill, PiPencilSimpleFill, PiTrashFill } from "react-icons/pi";
import UserModal from "../../../components/common/modals/user-modal/UserModal";
import Button from "../../../components/common/button/Button";
import CurrencyInput from "../../../components/common/inputs/CurrencyInput";
import {
  getApi,
  postApi,
  putApi,
  deleteApi,
} from "../../../services/api/globalApi";
import {
  successMessage,
  errorMessage,
} from "../../../utils/toastify-message/ToastifyMessage";
import { digitsThree } from "../../../utils/price";
import { monthNamesFa, monthOrder } from "./monthConstants";

const inputClassName =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";

export default function ManualSalesAdjustmentModal({
  isOpen,
  onClose,
  onSuccess,
}) {
  const userModalRef = useRef();
  const amountInputRef = useRef();
  const grossAmountInputRef = useRef();
  const [selectedUser, setSelectedUser] = useState(null);
  const [year, setYear] = useState("1404");
  const [month, setMonth] = useState("mordad");
  const [amount, setAmount] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const jalaliDate = `${year}/${String(
    monthOrder.indexOf(month) + 1
  ).padStart(2, "0")}`;

  const resetForm = () => {
    setAmount("");
    setGrossAmount("");
    setNote("");
    setEditingAdjustment(null);
    amountInputRef.current?.afterSubmit();
    grossAmountInputRef.current?.afterSubmit();
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null);
      resetForm();
      setHistory([]);
    }
  }, [isOpen]);

  const fetchHistory = async (userId) => {
    setLoadingHistory(true);
    try {
      const response = await getApi({
        path: `admin/reports/monthly-sales/${userId}/adjustments`,
      });
      setHistory(response?.data?.result || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!selectedUser) {
      setHistory([]);
      return;
    }
    fetchHistory(selectedUser._id);
  }, [selectedUser]);

  const handleUser = (data) => {
    setSelectedUser(data);
    resetForm();
    successMessage(`نماینده ${data?.name || data?.mobile} انتخاب شد`);
  };

  const startEdit = (item) => {
    setEditingAdjustment(item);
    setAmount(String(item.totalAmount ?? ""));
    setGrossAmount(String(item.grossAmount ?? ""));
    setNote(item.note || "");
  };

  const cancelEdit = () => {
    resetForm();
  };

  const handleDelete = async (item) => {
    if (!selectedUser) return;
    if (!window.confirm("این اصلاحیه حذف شود؟")) return;
    setDeletingId(item._id);
    try {
      await deleteApi({
        path: `admin/reports/monthly-sales/${selectedUser._id}/adjustments/${item._id}`,
      });
      successMessage("اصلاحیه حذف شد");
      if (editingAdjustment?._id === item._id) resetForm();
      await fetchHistory(selectedUser._id);
      onSuccess?.();
    } catch (error) {
      errorMessage(
        error?.response?.data?.errors?.[0]?.message || "خطا در حذف اصلاحیه"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async () => {
    const numericAmount = Number(amount);
    if (amount === "" || Number.isNaN(numericAmount)) {
      errorMessage("مبلغ خالص معتبر وارد کنید");
      return;
    }
    const numericGrossAmount = Number(grossAmount);
    if (grossAmount === "" || Number.isNaN(numericGrossAmount)) {
      errorMessage("مبلغ ناخالص معتبر وارد کنید");
      return;
    }

    if (editingAdjustment) {
      setSubmitting(true);
      try {
        await putApi({
          path: `admin/reports/monthly-sales/${selectedUser._id}/adjustments/${editingAdjustment._id}`,
          body: {
            totalAmount: numericAmount,
            grossAmount: numericGrossAmount,
            note: note || undefined,
          },
        });
        successMessage("اصلاحیه با موفقیت ویرایش شد");
        resetForm();
        await fetchHistory(selectedUser._id);
        onSuccess?.();
      } catch (error) {
        errorMessage(
          error?.response?.data?.errors?.[0]?.message ||
            "خطا در ویرایش اصلاحیه"
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!selectedUser) {
      errorMessage("ابتدا نماینده را انتخاب کنید");
      return;
    }

    setSubmitting(true);
    try {
      await postApi({
        path: "admin/reports/monthly-sales/adjustment",
        body: {
          userId: selectedUser._id,
          jalaliDate,
          totalAmount: numericAmount,
          grossAmount: numericGrossAmount,
          note: note || undefined,
        },
      });
      successMessage(
        `اصلاحیه ${digitsThree(numericAmount)} تومانی برای ${
          selectedUser.name || selectedUser.mobile
        } ثبت شد`
      );
      resetForm();
      onSuccess?.();
    } catch (error) {
      errorMessage(
        error?.response?.data?.errors?.[0]?.message || "خطا در ثبت اصلاحیه"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <UserModal ref={userModalRef} handleUser={handleUser} />
      <div
        className="fixed top-0 right-0 w-full h-full bg-black/40 z-30"
        onClick={onClose}
      ></div>
      <div className="w-[320px] md:w-[640px] max-h-[85vh] overflow-y-auto dark:bg-gray-900 bg-white shadow-md z-40 rounded-3xl p-6 fixed top-16 right-[50%] mr-[-160px] md:mr-[-320px]">
        <div className="w-full flex items-center justify-between pb-4 border-b-2 dark:text-white">
          <h4 className="text-lg font-semibold">
            افزودن اصلاحیه دستی فروش ماهانه
          </h4>
          <PiXFill
            onClick={onClose}
            size={26}
            className="text-red-400 transition-all hover:text-red-600 cursor-pointer"
          />
        </div>

        <div className="mt-4 flex flex-col gap-4">
          {editingAdjustment ? (
            <div className="rounded-lg bg-blue-50 dark:bg-gray-800 px-4 py-3 text-sm text-blue-700 dark:text-blue-300 flex items-center justify-between">
              <span>
                در حال ویرایش اصلاحیه{" "}
                {monthNamesFa[editingAdjustment.month] ||
                  editingAdjustment.month}{" "}
                {editingAdjustment.year} برای{" "}
                {selectedUser?.name || selectedUser?.mobile}
              </span>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-xs font-medium text-gray-500 hover:underline"
              >
                انصراف از ویرایش
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <span
                  onClick={() => userModalRef.current?.showModal()}
                  className="absolute w-full h-full z-10 cursor-pointer"
                ></span>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  نماینده *
                </label>
                <input
                  value={
                    selectedUser
                      ? `${selectedUser.name || ""} - ${selectedUser.mobile}`
                      : ""
                  }
                  readOnly
                  placeholder="برای انتخاب نماینده کلیک کنید"
                  className={inputClassName}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    سال (شمسی) *
                  </label>
                  <input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className={inputClassName}
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    ماه *
                  </label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className={inputClassName}
                  >
                    {monthOrder.map((m) => (
                      <option key={m} value={m}>
                        {monthNamesFa[m]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                مبلغ خالص (تومان) *
              </label>
              <CurrencyInput
                ref={amountInputRef}
                name="amount"
                inputValue={amount}
                onChange={(value) => setAmount(value)}
                placeholder="مثال: 10,000,000"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                مبلغ ناخالص (تومان) *
              </label>
              <CurrencyInput
                ref={grossAmountInputRef}
                name="grossAmount"
                inputValue={grossAmount}
                onChange={(value) => setGrossAmount(value)}
                placeholder="مثال: 10,000,000"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              توضیح / دلیل
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className={inputClassName}
            />
          </div>

          {selectedUser && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">
                تاریخچه اصلاحیه‌های این نماینده
              </p>
              {loadingHistory ? (
                <p className="text-xs text-gray-400">در حال بارگذاری...</p>
              ) : history.length ? (
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {history.map((item) => (
                    <div
                      key={item._id}
                      className="px-3 py-2 text-xs flex justify-between items-center gap-2"
                    >
                      <span className="min-w-0">
                        {monthNamesFa[item.month] || item.month} {item.year}
                        {item.note ? ` — ${item.note}` : ""}
                      </span>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-medium text-green-600 whitespace-nowrap">
                          {digitsThree(item.totalAmount)} تومان
                        </span>
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          title="ویرایش"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <PiPencilSimpleFill size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={deletingId === item._id}
                          title="حذف"
                          className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        >
                          <PiTrashFill size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  اصلاحیه‌ی قبلی برای این نماینده ثبت نشده است
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3 justify-start mt-2">
            <Button
              type="button"
              text={
                submitting
                  ? "در حال ذخیره..."
                  : editingAdjustment
                  ? "ذخیره ویرایش"
                  : "ثبت"
              }
              color="success"
              onClick={handleSubmit}
              disabled={submitting}
            />
            <Button type="button" text="انصراف" color="danger" onClick={onClose} />
          </div>
        </div>
      </div>
    </>
  );
}
